import { useCallback, useEffect, useRef, useState } from 'react'
import type { FC } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { message } from 'antd'

import { apiRequest, apiRequestRawNoVisitorInfoThrottle, setNewApiToken } from '@/helpers/request'
import { flushCordovaCookies } from '@/helpers/cordova-cookie-manager'
import { rqKeys } from '@/react-query-keys'
import { routes } from '@/router/routes'
import { phoneMask } from '@/components/input/helpers'
import { Button, Flex, Skeleton } from '@/components'
import { useAppStore } from '@/store'
import { cleanUpPhone, getMs, isCordovaApp } from '@/helpers'
import type { T_UniRes } from '@/types'
import { isOfflineAuthError } from '../helpers'
import { useAuthStore } from '../../store'
import { readPaidCallSession } from '../../paid-call-session'
import { useGetPhoneToCallMutation } from '../initial-screen/use-get-phone-to-call-mutation'
import { E_Colors } from '@/helpers/colors'


const OUTGOING_CALL_SMS_FALLBACK_MESSAGE_KEY = 'outgoing-call-sms-fallback'

export const OutgoingCall: FC = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const setToAuthorized = useAppStore((s) => s.setToAuthorized)
  const userPhone = useAuthStore((s) => s.userPhone)
  const setUserPhone = useAuthStore((s) => s.setUserPhone)
  const setCurrentStep = useAuthStore((s) => s.setCurrentStep)
  const outgoingPhone = useAuthStore((s) => s.outgoingPhone)
  const outgoingMessageNumber = useAuthStore((s) => s.outgoingMessageNumber)
  const [ isAlternativeAuthAvailable, setIsAlternativeAuthAvailable ] = useState(false)
  const isAuthCompletedRef = useRef(false)
  const isPollingInFlightRef = useRef(false)
  const isAuthFinalizingRef = useRef(false)
  const isPollingTimeoutMessageShownRef = useRef(false)
  const isOutgoingMessageDeliveredRef = useRef(false)
  const isPollingStoppedByFallbackRef = useRef(false)
  const confirmFailureRetryPollingLeftRef = useRef<number | null>(null)
  const fallbackMessageClickHandlerRef = useRef<((e: Event) => void) | null>(null)

  const {
    isGetPhoneToCallPending,
  } = useGetPhoneToCallMutation()

  const showSmsFallback = useCallback((fallbackText?: string) => {
    setIsAlternativeAuthAvailable(true)
    if (!isPollingTimeoutMessageShownRef.current) {
      isPollingTimeoutMessageShownRef.current = true
      message.error({
        content: fallbackText || "К сожалению, звонок не поступил. Попробуйте авторизоваться через пункт 'Получить входящее sms'",
        duration: 0,
        key: OUTGOING_CALL_SMS_FALLBACK_MESSAGE_KEY,
      })

      if (!fallbackMessageClickHandlerRef.current) {
        const clickHandler = () => {
          message.destroy(OUTGOING_CALL_SMS_FALLBACK_MESSAGE_KEY)
          if (fallbackMessageClickHandlerRef.current) {
            window.removeEventListener('pointerdown', fallbackMessageClickHandlerRef.current, true)
            fallbackMessageClickHandlerRef.current = null
          }
        }

        fallbackMessageClickHandlerRef.current = clickHandler
        window.addEventListener('pointerdown', clickHandler, true)
      }
    }
  }, [])

  useEffect(() => {
    return () => {
      if (fallbackMessageClickHandlerRef.current) {
        window.removeEventListener('pointerdown', fallbackMessageClickHandlerRef.current, true)
        fallbackMessageClickHandlerRef.current = null
      }
      message.destroy(OUTGOING_CALL_SMS_FALLBACK_MESSAGE_KEY)
    }
  }, [])

  const finishAuthorization = useCallback(async () => {
    if (isAuthCompletedRef.current || isAuthFinalizingRef.current) return
    isAuthFinalizingRef.current = true

    try {
      try {
        // Прогоняем через apiRequest, чтобы maybeStoreVisitorToken сохранил refresh-токен
        // из client/visitor/info перед переходом на home.
        await apiRequest<T_UniRes<any>>({
          method: 'client/visitor/info',
        })
      } catch (e) {
        console.warn('[auth] client/visitor/info sync failed after outgoing call', e)
      }

      const retryDelaysMs = [ 0, 1500, 4000 ]
      let generatedToken: string | null = null

      for (const delayMs of retryDelaysMs) {
        if (delayMs > 0) {
          await new Promise<void>((resolve) => {
            window.setTimeout(resolve, delayMs)
          })
        }

        try {
          const tokenRes = await apiRequest<{ data?: { token?: string } }>({
            method: 'client/token/generate',
          })
          const token = tokenRes?.data?.token
          if (token) {
            generatedToken = token
            break
          }
        } catch (e) {
          console.warn('[auth] client/token/generate failed after outgoing call', e)
        }
      }

      // Жёсткий режим: не завершаем авторизацию, пока не получили новый NEW API токен.
      if (!generatedToken) return
      setNewApiToken(generatedToken)

      if (isCordovaApp) {
        await flushCordovaCookies()
      }

      setCurrentStep('initialScreen')
      setUserPhone(phoneMask)

      isAuthCompletedRef.current = true
      setToAuthorized()
      navigate(routes.home, { replace: true })

      await queryClient.invalidateQueries({ queryKey: [ rqKeys.userData ] })
      await queryClient.invalidateQueries({ queryKey: [ rqKeys.userBalance ] })
    } finally {
      isAuthFinalizingRef.current = false
    }
  }, [ navigate, queryClient, setCurrentStep, setToAuthorized, setUserPhone ])

  const pollAuthorizationStatus = useCallback(async () => {
    if (isAuthCompletedRef.current || isPollingInFlightRef.current || isPollingStoppedByFallbackRef.current) return
    isPollingInFlightRef.current = true

    try {
      if (!outgoingMessageNumber) {
        isPollingStoppedByFallbackRef.current = true
        showSmsFallback("Не удалось получить номер сообщения для проверки звонка. Попробуйте авторизоваться через пункт 'Получить входящее sms'")
        return
      }

      if (!isOutgoingMessageDeliveredRef.current) {
        const deliveryRes = await apiRequest<T_UniRes<any>>({
          method: 'sender/message/info',
          param: {
            id: outgoingMessageNumber,
          },
        })

        const deliveredRaw = deliveryRes?.data?.is_delivered
        const isDelivered = (
          deliveredRaw === true
          || deliveredRaw === 1
          || deliveredRaw === '1'
          || deliveredRaw === 'true'
        )

        if (!isDelivered) return

        // Как только получили is_delivered=true, прекращаем опрос sender/message/info.
        isOutgoingMessageDeliveredRef.current = true
      }

      const confirmRes = await apiRequest<T_UniRes<any>>({
        method: 'client/security/confirm',
        param: {
          type: 'action',
        },
      })

      let isConfirmOk = confirmRes?.code === 'ok'

      if (!isConfirmOk) {
        if (confirmFailureRetryPollingLeftRef.current === null) {
          confirmFailureRetryPollingLeftRef.current = 2
          return
        }

        if (confirmFailureRetryPollingLeftRef.current > 0) {
          confirmFailureRetryPollingLeftRef.current -= 1
          if (confirmFailureRetryPollingLeftRef.current === 0) {
            isPollingStoppedByFallbackRef.current = true
            showSmsFallback()
          }
          return
        }

        if (confirmFailureRetryPollingLeftRef.current === 0) {
          isPollingStoppedByFallbackRef.current = true
          showSmsFallback()
          return
        }
      }
      confirmFailureRetryPollingLeftRef.current = null

      const visitorInfoRes = await apiRequestRawNoVisitorInfoThrottle<T_UniRes<any>>({
        method: 'client/visitor/info',
      })

      if (visitorInfoRes?.code === 'ok') {
        await finishAuthorization()
      }
    } catch (error) {
      // В офлайне просто ждём следующего тика polling.
      if (isOfflineAuthError(error)) return
      console.warn('[auth] outgoing call polling failed', error)
    } finally {
      isPollingInFlightRef.current = false
    }
  }, [ finishAuthorization, outgoingMessageNumber, showSmsFallback ])

  useEffect(() => {
    setIsAlternativeAuthAvailable(false)
    isPollingTimeoutMessageShownRef.current = false
    isPollingStoppedByFallbackRef.current = false
    confirmFailureRetryPollingLeftRef.current = null

    const startedAt = readPaidCallSession()?.issuedAt ?? Date.now()
    const smsAvailabilityDelayMs = Math.max(0, getMs('2m') - (Date.now() - startedAt))
    const fastPollingDurationMs = getMs('2m')
    const fullPollingDurationMs = getMs('5m')
    const fastIntervalMs = getMs('5s')
    const slowIntervalMs = getMs('10s')

    let timeoutId: number | null = null
    let smsAvailabilityTimeoutId: number | null = null
    let isStopped = false

    const scheduleNextTick = (delayMs: number) => {
      if (isStopped) return
      timeoutId = window.setTimeout(() => {
        void tick()
      }, delayMs)
    }

    const tick = async () => {
      if (isStopped || isAuthCompletedRef.current || isPollingStoppedByFallbackRef.current) return

      const elapsedMs = Date.now() - startedAt
      if (elapsedMs >= fullPollingDurationMs) {
        showSmsFallback()
        return
      }

      await pollAuthorizationStatus()

      if (isStopped || isAuthCompletedRef.current || isPollingStoppedByFallbackRef.current) return

      const elapsedAfterTickMs = Date.now() - startedAt
      const nextDelayMs = elapsedAfterTickMs < fastPollingDurationMs
        ? fastIntervalMs
        : slowIntervalMs

      scheduleNextTick(nextDelayMs)
    }

    void tick()
    smsAvailabilityTimeoutId = window.setTimeout(() => {
      if (!isStopped && !isAuthCompletedRef.current) {
        setIsAlternativeAuthAvailable(true)
      }
    }, smsAvailabilityDelayMs)

    return () => {
      isStopped = true
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
      if (smsAvailabilityTimeoutId !== null) {
        window.clearTimeout(smsAvailabilityTimeoutId)
      }
    }
  }, [ pollAuthorizationStatus, showSmsFallback ])

  const handleCall = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isCordovaApp && outgoingPhone) {
      e.preventDefault()
      const phone = cleanUpPhone(outgoingPhone)
      window.open(`tel:${phone}`, '_system')
    }
  }


  return <>
    <Flex
      gap={20}
      justify='center'
      align='center'
      vertical
      style={{ width: '100%' }}
    >
      <div style={{ fontWeight: 500, fontSize: '33px', textAlign: 'center' }}>
        Исходящий звонок
      </div>

      {isGetPhoneToCallPending ? (
        <div style={{ width: '100%' }}>
          <Skeleton count={1} title={false} />
        </div>
      ) : (
        <p style={{ fontSize: '14px', textAlign: 'center', lineHeight: 1.6 }}>
          Пожалуйста, позвоните с указанного номера
          &nbsp;
          <span style={{ whiteSpace: 'pre' }}>
            {userPhone}
          </span>
          &nbsp;
          <span
            onClick={() => setCurrentStep('initialScreen')}
            style={{
              color: E_Colors.EraBlue,
              fontWeight: 600,
            }}
          >
            (изменить)
          </span>
          &nbsp;
          на номер:

          <a
            href={`tel:${outgoingPhone}`}
            onClick={handleCall}
            style={{
              whiteSpace: 'pre',
              fontSize: '24px',
              fontWeight: 600,
              display: 'block',
              margin: '6px 0 2px'
            }}
          >
            {outgoingPhone}
          </a>

          Ваш звонок сбросится, а затем должна начаться автоматическая авторизация в программе.

          <br />

          <strong>Звонок бесплатный</strong>.
          <br />
          <strong>Ожидаем вашего звонка</strong>
        </p>
      )}

      <a
        href={`tel:${outgoingPhone}`}
        onClick={handleCall}
        style={{ display: 'block', width: '100%' }}
      >
        <Button
          type='primary'
          block
        >
          Позвонить
        </Button>
      </a>

      <div style={{ display: isAlternativeAuthAvailable ? 'block' : 'none', width: '100%' }}>
        <p style={{ fontSize: '14px', textAlign: 'center', lineHeight: 1.6 }}>
          Если у вас не получается позвонить на указанный номер, то можно воспользоваться альтернативным вариантом:
        </p>

        <Button
          onClick={() => {
            isPollingStoppedByFallbackRef.current = true
            setCurrentStep('incomingCallOrSms')
          }}
          block
        >
          Получить входящее sms
        </Button>
      </div>
    </Flex>
  </>
}

